import {
  usesInventorySelector,
  hasUseReturnOrdersFeatureSelector,
  hasUseMOsFeatureSelector,
  useAnalyticsSelector,
  hasUseStocktakeFeatureSelector,
} from '../../../data/company.js'
import {canUseReportsPageSelector} from '../../ReportsPage/reportsPageSelectors.js'
import Checkbox from '../Common/Checkbox.js'
import {useSelector} from '../../../store.js'
import {updatePermissions} from './usersFunctions.js'
import {ExportOrdersParamsSummary} from '../../ReportsPage/ReportForms/ExportOrders.js'
import {useCallback} from 'react'
import {showOrderQueryFilterModal} from '../../OrderListPage/Modals/OrderListFilterModal/orderListFilterModalFunctions.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'

export const PERMISSION_FILTER = 'PERMISSION_FILTER'

function permissionSectionsSelector(state) {
  return [
    {
      value: 'order',
      display: 'Orders',
      children: [
        {value: 'import_from_cart', display: 'Import from sales channels'},
        {
          value: 'force_verify_packing',
          display: 'Allow force verification in pick/pack modal',
        },
        {
          value: 'create_label_without_contents_verified_tag',
          display:
            'Allow shipping label creation without Contents Verified tag',
        },
        {value: 'manage_tag', display: 'Create and edit tags'},
        {
          value: 'manage_tag_on_order',
          display: 'Add and remove tags on orders',
        },
        {
          value: 'manage_revision',
          display: 'Accept and deny order revisions',
        },
        {
          value: 'financials',
          display: 'View and edit order financials',
        },
        {
          value: 'manage_filter_config',
          display: 'Create and edit saved filters',
        },
        {
          value: 'create_quick_ship',
          display: 'Create Quick Ship labels',
        },
        {
          value: 'filter',
          display: 'Order Restrictions',
          type: 'filter',
          FilterDisplay({filter}) {
            const onClick = useCallback(async () => {
              const updatedFilter = await showOrderQueryFilterModal({
                query: filter,
                type: PERMISSION_FILTER,
              })

              updatePermissions(['order', 'filter'], updatedFilter)
            }, [filter])

            return (
              <>
                <div className="alert alert--neutral alert--lg no-bg margin-top-20 margin-bottom-20">
                  <div className="flex margin-bottom-10">
                    <strong className="fs-00 lh-md margin-right-10 flex--justify-col">
                      Restrict Order Access
                    </strong>
                    <div>
                      <ButtonPrimary
                        isOutlined
                        size="xx-sm"
                        className="flex--justify-col"
                        onClick={onClick}
                      >
                        {Object.keys(filter).length === 0
                          ? 'Create Subset'
                          : 'Edit Subset'}
                      </ButtonPrimary>
                    </div>
                  </div>
                  <div className="wrap--user-restriction-filters">
                    <ExportOrdersParamsSummary
                      type="permissions"
                      params={filter}
                      emptyLabel="User currently has unrestricted access to all orders. "
                    />
                  </div>
                </div>
              </>
            )
          },
        },
      ],
    },
    ...(hasUseReturnOrdersFeatureSelector(state)
      ? [
          {
            value: 'return_order',
            display: 'Return Orders',
            children: [
              {value: 'manage_tag', display: 'Create and edit tags'},
              {
                value: 'manage_tag_on_return_order',
                display: 'Add and remove tags on return orders',
              },
            ],
          },
        ]
      : []),
    {
      value: 'product',
      display: 'Products',
      children: [
        {value: 'import_from_cart', display: 'Import from sales channels'},
        {value: 'manage_tag', display: 'Create and edit tags'},
        {value: 'transfer', display: 'Create and view inventory transfers'},
        {
          value: 'manage_tag_on_product',
          display: 'Add and remove tags on products',
        },
        ...(usesInventorySelector(state)
          ? [
              {
                value: 'writeback_inventory',
                display: 'Write inventory back to sales channels',
              },
              {
                value: 'update_inventory',
                display: 'Update/reconcile inventory on products',
              },
              {
                value: 'financials',
                display: 'View and edit product financials',
              },
            ]
          : []),
      ],
    },
    ...(usesInventorySelector(state)
      ? [
          {
            value: 'purchase_order',
            display: 'POs',
            children: [
              {value: 'manage_tag', display: 'Create and edit tags'},
              {
                value: 'manage_tag_on_purchase_order',
                display: 'Add and remove tags on purchase orders',
              },
              {
                value: 'financials',
                display: 'View and edit purchase order financials',
              },
            ],
          },
        ]
      : []),
    ...(hasUseMOsFeatureSelector(state)
      ? [
          {
            value: 'manufacturing_order',
            display: 'MFG Orders',
            children: [
              {value: 'manage_tag', display: 'Create and edit tags'},
              {
                value: 'manage_tag_on_manufacturing_order',
                display: 'Add and remove tags on MFG orders',
              },
            ],
          },
        ]
      : []),
    ...(hasUseStocktakeFeatureSelector(state)
      ? [
          {
            value: 'stocktake',
            display: 'Stocktake',
            children: [
              {value: 'manage_tag', display: 'Create and edit tags'},
              {
                value: 'manage_tag_on_stocktake',
                display: 'Add and remove tags on Stocktakes',
              },
            ],
          },
        ]
      : []),
    ...(useAnalyticsSelector(state)
      ? [
          {
            value: 'analytics',
            display: 'Analytics',
            children: [
              {value: 'financial', display: 'View financial stats'},
              {value: 'shipping', display: 'View shipping stats'},
              {value: 'inventory', display: 'View inventory stats'},
              {value: 'product', display: 'View product stats'},
              {value: 'customer', display: 'View customer stats'},
            ],
          },
        ]
      : []),
    ...(canUseReportsPageSelector(state)
      ? [{value: 'reports', display: 'Reports'}]
      : []),
    {value: 'settings', display: 'Settings'},
  ]
}

function Permission({
  permission,
  permissions,
  id = `permission_${permission.value}`,
  path = [permission.value],
  level = 0,
}) {
  const {value, display, children, type = 'boolean', FilterDisplay} = permission

  const defaultPerms = children
    ? children.reduce((prev, child) => {
        prev[child.value] = true
        return prev
      }, {})
    : true

  const perms =
    children &&
    (permissions[value] === true || permissions[value] === undefined)
      ? defaultPerms
      : permissions[value]

  const item =
    type === 'boolean' ? (
      <Checkbox
        id={id}
        label={display}
        checked={permissions[value] !== false}
        onChange={(isChecked) => {
          const newPerms = children && isChecked ? defaultPerms : isChecked
          updatePermissions(path, newPerms)
        }}
      >
        {perms &&
          children &&
          children.map((child) => (
            <Permission
              key={child.value}
              permission={child}
              permissions={perms}
              id={`${id}_${child.value}`}
              path={[...path, child.value]}
              level={level + 1}
            />
          ))}
      </Checkbox>
    ) : type === 'filter' ? (
      <FilterDisplay filter={perms || {}} />
    ) : null

  return level === 0 ? (
    <li className="form-list-item bigger padding-bottom-0 padding-left-0 margin-bottom-0 margin-top-15">
      {item}
    </li>
  ) : (
    item
  )
}

export default function Permissions({permissions}) {
  const permissionSections = useSelector(permissionSectionsSelector)

  if (!permissions) {
    return null
  }

  return (
    <div className="medium-8 columns">
      <p className="divider--top fs-01 margin-bottom-10">
        <strong>Sections and features this user can access:</strong>
      </p>
      <div className="list--form">
        {permissionSections.map((permission) => (
          <div
            key={permission.value}
            className="list__item--form margin-bottom-10"
          >
            <Permission permission={permission} permissions={permissions} />
          </div>
        ))}
      </div>
    </div>
  )
}
